<template>
  <div
    ref="group"
    :class="['ui-segmented', `ui-segmented--${size}`, { 'ui-segmented--block': block }]"
    role="radiogroup"
    :aria-label="label || undefined"
    @keydown="onKeydown"
  >
    <button
      v-for="option in options"
      :key="option.value"
      type="button"
      role="radio"
      :class="['ui-segmented__item', { 'is-active': option.value === model }]"
      :aria-checked="option.value === model ? 'true' : 'false'"
      :tabindex="option.value === focusable ? 0 : -1"
      :disabled="option.disabled"
      :title="option.title"
      :aria-label="option.title"
      :data-value="option.value"
      @click="choose(option.value)"
    >
      {{ option.label }}
    </button>
  </div>
</template>

<script setup lang="ts">
import { computed, useTemplateRef } from 'vue'

import type { SegmentedOption } from './types'

/**
 * One of a few values, all in sight: an alignment, a number of columns, a
 * mode. Quantum's segmented tabs, as a value picker rather than a tab list:
 * a pill holding the options, the chosen one filled blue.
 *
 * A radio group to assistive technology and to the keyboard: Tab reaches
 * the chosen option, the arrows move the choice, Home and End jump to the
 * ends, and a disabled option is passed over.
 */
const props = withDefaults(
  defineProps<{
    options: readonly SegmentedOption[]
    size?: 'sm' | 'md'
    /** Stretches across its container, the options sharing the width. */
    block?: boolean
    /** Names the group, for a screen reader, when nothing visible does. */
    label?: string
  }>(),
  { size: 'md', block: false, label: '' },
)

const model = defineModel<string | undefined>({ default: undefined })

const group = useTemplateRef<HTMLElement>('group')

const enabled = computed(() => props.options.filter((option) => !option.disabled))

/** What Tab lands on: the chosen option, or the first that can be chosen. */
const focusable = computed(() =>
  enabled.value.some((option) => option.value === model.value)
    ? model.value
    : enabled.value[0]?.value,
)

function choose(value: string): void {
  model.value = value
}

const STEPS: Record<string, (index: number, count: number) => number> = {
  ArrowRight: (at, count) => (at + 1) % count,
  ArrowDown: (at, count) => (at + 1) % count,
  ArrowLeft: (at, count) => (at - 1 + count) % count,
  ArrowUp: (at, count) => (at - 1 + count) % count,
  Home: () => 0,
  End: (_, count) => count - 1,
}

function onKeydown(event: KeyboardEvent): void {
  const step = STEPS[event.key]
  const choices = enabled.value
  if (!step || choices.length === 0) return
  event.preventDefault()
  const from = Math.max(
    choices.findIndex((option) => option.value === model.value),
    0,
  )
  const next = choices[step(from, choices.length)]
  if (!next) return
  choose(next.value)
  const radios = group.value?.querySelectorAll<HTMLButtonElement>('[role="radio"]') ?? []
  Array.from(radios)
    .find((radio) => radio.dataset['value'] === next.value)
    ?.focus()
}
</script>

<style scoped>
.ui-segmented {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding: 3px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-pill);
  background: var(--color-surface);
}

.ui-segmented--block {
  display: flex;
  width: 100%;
}

.ui-segmented__item {
  flex: none;
  border: 0;
  border-radius: var(--radius-pill);
  background: none;
  color: var(--color-text-muted);
  font-family: inherit;
  font-weight: var(--weight-semibold);
  white-space: nowrap;
  cursor: pointer;
  transition:
    background-color var(--transition-base) ease,
    color var(--transition-base) ease;
}

.ui-segmented--block .ui-segmented__item {
  flex: 1;
  min-width: 0;
}

.ui-segmented--sm .ui-segmented__item {
  padding: 3px 10px;
  font-size: var(--text-xs);
}

.ui-segmented--md .ui-segmented__item {
  padding: 5px 13px;
  font-size: var(--text-sm);
}

.ui-segmented__item:hover:not(:disabled, .is-active) {
  color: var(--color-text);
}

.ui-segmented__item:focus-visible {
  outline: none;
  box-shadow: var(--shadow-focus);
}

.ui-segmented__item:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.ui-segmented__item.is-active {
  background: var(--color-primary);
  color: var(--color-on-primary);
}
</style>
